import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import { plansService } from '../../services/plansService';

const PlansManagement = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    coverImage: '',
    description: '',
    price: 0,
    displayOrder: 0,
    isActive: true
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await plansService?.getAll();
      setPlans(data);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan?.name || '',
        coverImage: plan?.coverImage || '',
        description: plan?.description || '',
        price: plan?.price || 0,
        displayOrder: plan?.displayOrder || 0,
        isActive: plan?.isActive !== undefined ? plan?.isActive : true
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        coverImage: '',
        description: '',
        price: 0,
        displayOrder: 0,
        isActive: true
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setError('');
    setSuccess('');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.name || !formData?.coverImage || !formData?.description) {
      setError('Nome, imagem de capa e descrição são obrigatórios');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (editingPlan) {
        await plansService?.update(editingPlan?.id, formData);
        setSuccess('Plano atualizado com sucesso!');
      } else {
        await plansService?.create(formData);
        setSuccess('Plano criado com sucesso!');
      }

      loadPlans();
      setTimeout(() => {
        handleCloseModal();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Erro ao salvar plano');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Tem certeza que deseja excluir este plano?')) {
      return;
    }

    try {
      setLoading(true);
      await plansService?.delete(planId);
      setSuccess('Plano excluído com sucesso!');
      loadPlans();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Erro ao excluir plano');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Gerenciar Planos - UP MIDIA Admin</title>
        <meta name="description" content="Gerencie planos de assinatura e produtos" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="container mx-auto px-4 py-4">
            <AdminNavigation />
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Gerenciar Planos
              </h1>
              <p className="text-muted-foreground">
                Crie e gerencie planos de assinatura e produtos
              </p>
            </div>
            <Button
              onClick={() => handleOpenModal()}
              iconName="Plus"
              className="glow-primary"
            >
              Novo Plano
            </Button>
          </div>

          {error && !showModal && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
              {error}
            </div>
          )}

          {success && !showModal && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500 rounded-lg text-green-500">
              {success}
            </div>
          )}

          {loading && !showModal ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin glow-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans?.map((plan) => (
                <div
                  key={plan?.id}
                  className="bg-card rounded-lg overflow-hidden glow-primary hover:glow-primary-hover transition-smooth"
                >
                  <div className="relative h-48">
                    <Image
                      src={plan?.coverImage}
                      alt={`Capa do plano ${plan?.name}`}
                      className="w-full h-full object-cover"
                    />
                    {!plan?.isActive && (
                      <div className="absolute top-3 right-3 px-3 py-1 bg-red-500 text-white text-xs font-medium rounded">
                        Inativo
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                      {plan?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {plan?.description}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-accent">
                        R$ {plan?.price?.toFixed(2)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Ordem: {plan?.displayOrder}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleOpenModal(plan)}
                        iconName="Edit"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        Editar
                      </Button>
                      <Button
                        onClick={() => handleDelete(plan?.id)}
                        iconName="Trash2"
                        variant="destructive"
                        size="sm"
                      >
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && plans?.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="Package" size={32} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                Nenhum plano cadastrado
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Comece criando seu primeiro plano de assinatura
              </p>
              <Button onClick={() => handleOpenModal()} iconName="Plus">
                Criar Primeiro Plano
              </Button>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto glow-primary">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground">
                {editingPlan ? 'Editar Plano' : 'Novo Plano'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500 rounded text-sm text-red-500">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500 rounded text-sm text-green-500">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nome do Plano *
                </label>
                <Input
                  value={formData?.name}
                  onChange={(e) => handleChange('name', e?.target?.value)}
                  placeholder="Ex: Plano Mensal Premium"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  URL da Imagem de Capa *
                </label>
                <Input
                  value={formData?.coverImage}
                  onChange={(e) => handleChange('coverImage', e?.target?.value)}
                  placeholder="https://exemplo.com/imagem.jpg"
                  required
                />
                {formData?.coverImage && (
                  <div className="mt-2">
                    <Image
                      src={formData?.coverImage}
                      alt="Preview da capa do plano"
                      className="w-full h-48 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descrição *
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => handleChange('description', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                  placeholder="Descreva os benefícios e recursos do plano..."
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Preço (R$) *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData?.price}
                    onChange={(e) => handleChange('price', parseFloat(e?.target?.value) || 0)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Ordem de Exibição
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={formData?.displayOrder}
                    onChange={(e) => handleChange('displayOrder', parseInt(e?.target?.value) || 0)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData?.isActive}
                  onChange={(e) => handleChange('isActive', e?.target?.checked)}
                  className="w-4 h-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-foreground">
                  Plano Ativo
                </label>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 glow-primary"
                >
                  {loading ? 'Salvando...' : editingPlan ? 'Atualizar Plano' : 'Criar Plano'}
                </Button>
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  variant="ghost"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PlansManagement;